import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Shirt, PenLine, Eraser, Loader2, PackageCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

const SignCanvas = ({ onChange }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => {
    const c = ref.current; const r = c.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * (c.width / r.width), y: (src.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = ref.current.getContext('2d'); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = pos(e); const ctx = ref.current.getContext('2d'); ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#1e293b'; ctx.lineTo(x, y); ctx.stroke(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL('image/png')); };
  const clear = () => { const c = ref.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={480} height={150}
        className="w-full border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 touch-none cursor-crosshair"
        style={{ height: 130 }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="equipment-sign-canvas" />
      <button type="button" onClick={clear} className="text-[11px] text-gray-400 hover:text-red-500 flex items-center gap-1 mt-1">
        <Eraser className="w-3 h-3" /> Effacer la signature
      </button>
    </div>
  );
};

const CAT_LABELS = { vetement: 'Vêtement', chaussure: 'Chaussure', accessoire: 'Accessoire', materiel: 'Matériel', autre: 'Autre' };

// Iter 461 : popup BLOQUANTE de signature de reception d'equipement.
// L'employe doit consulter et signer chaque remise avant de poursuivre.
export default function EquipmentSignPopup() {
  const [pending, setPending] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchPending = () => {
    axios.get(`${API}/me/equipment-signatures/pending`, { headers })
      .then(r => { setPending(r.data || []); setLoaded(true); })
      .catch(() => setLoaded(true));
  };
  useEffect(() => { fetchPending(); }, []); // eslint-disable-line

  if (!loaded || pending.length === 0) return null;
  const a = pending[0];
  const eq = a.equipment || {};
  const photo = eq.photo || a.delivery_photo;

  const sign = async () => {
    if (!signature) { toast.error('Votre signature est requise'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/me/equipment-signatures/${a.id}/sign`, { signature }, { headers });
      toast.success('Réception signée — merci !');
      setSignature(null);
      setPending(prev => prev.slice(1));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="equipment-sign-popup">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md my-6 p-5">
        <div className="flex items-center gap-2 mb-1">
          <PackageCheck className="w-5 h-5 text-brand-blue" />
          <h2 className="font-heading font-bold text-brand-blue text-lg">Réception d'équipement</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Un équipement vous a été remis. Vous devez confirmer sa réception en signant pour continuer.
          {pending.length > 1 && <span className="font-semibold"> ({pending.length} en attente)</span>}
        </p>

        <div className="rounded-xl border-2 border-gray-100 p-3 mb-4">
          <div className="flex gap-3">
            <div className="w-20 h-20 rounded-lg bg-gray-100 border overflow-hidden flex items-center justify-center shrink-0">
              {photo ? <img src={photo} alt={a.equipment_name} className="w-full h-full object-cover" data-testid="equipment-sign-photo" /> : <Shirt className="w-7 h-7 text-gray-300" />}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-gray-900" data-testid="equipment-sign-name">{a.equipment_name}</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {eq.category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">{CAT_LABELS[eq.category] || eq.category}</span>}
                {eq.size && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border">Taille {eq.size}</span>}
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-semibold">Quantité : {a.quantity}</span>
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5">
                Remis par <strong>{a.attributed_by_name || 'votre responsable'}</strong> le {new Date(a.attributed_at).toLocaleDateString('fr-FR')}
              </p>
            </div>
          </div>
        </div>

        <label className="text-xs text-gray-600 block mb-1 flex items-center gap-1">
          <PenLine className="w-3 h-3" /> Ma signature <span className="text-gray-400">— je reconnais avoir bien reçu cet équipement</span>
        </label>
        <SignCanvas onChange={setSignature} />

        <Button onClick={sign} disabled={submitting || !signature}
          className="w-full mt-3 bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="equipment-sign-submit">
          {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Je signe la réception
        </Button>
        <p className="text-[10px] text-gray-400 text-center mt-2">Une copie signée sera disponible dans vos documents.</p>
      </div>
    </div>
  );
}
